/**
 * Action Log View - Admin
 * View system activity logs with filtering, export, and real-time updates
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ScrollText,
  Search,
  Download,
  Filter,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  RefreshCw,
  ChevronDown,
  Pause,
  Play,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface LogEntry {
  id: string;
  timestamp: string;
  category: string;
  action: string;
  details: string;
  severity: "info" | "warning" | "error" | "success";
  source: string;
  userId?: string;
  userAgent?: string;
}

const LOG_CATEGORIES = [
  "auth",
  "security",
  "api",
  "mcp",
  "agi",
  "kernel",
  "convex",
  "data",
  "user",
  "system",
  "error",
];

const SEVERITY_COLORS = {
  info: "text-blue-500 bg-blue-500/10",
  warning: "text-amber-500 bg-amber-500/10",
  error: "text-red-500 bg-red-500/10",
  success: "text-green-500 bg-green-500/10",
};

const SEVERITY_ICONS = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
  success: CheckCircle,
};

export function ActionLogView() {
  const { addLogEntry } = useDashboardStore();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [isRealTime, setIsRealTime] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockLogs: LogEntry[] = [
        {
          id: "1",
          timestamp: "2025-12-29T18:00:00Z",
          category: "auth",
          action: "Login",
          details: "User authenticated via passkey",
          severity: "success",
          source: "auth-gate",
          userId: "u1",
        },
        {
          id: "2",
          timestamp: "2025-12-29T17:55:00Z",
          category: "agi",
          action: "Query Executed",
          details: "AGI query processed in 1.2s",
          severity: "info",
          source: "agi-service",
        },
        {
          id: "3",
          timestamp: "2025-12-29T17:50:00Z",
          category: "security",
          action: "API Key Validated",
          details: "API key sk-proj-*** validated successfully",
          severity: "info",
          source: "api-keys",
        },
        {
          id: "4",
          timestamp: "2025-12-29T17:45:00Z",
          category: "mcp",
          action: "Server Connected",
          details: "MCP server 'File System' connected",
          severity: "success",
          source: "mcp-manager",
        },
        {
          id: "5",
          timestamp: "2025-12-29T17:40:00Z",
          category: "error",
          action: "Connection Failed",
          details: "Failed to connect to database: Connection timeout",
          severity: "error",
          source: "kernel",
        },
        {
          id: "6",
          timestamp: "2025-12-29T17:35:00Z",
          category: "security",
          action: "API Key Revoked",
          details: "API key sk-proj-*** revoked by admin",
          severity: "warning",
          source: "api-keys",
        },
        {
          id: "7",
          timestamp: "2025-12-29T17:30:00Z",
          category: "data",
          action: "Query Executed",
          details: "SQL query executed in 45ms",
          severity: "info",
          source: "data-view",
        },
        {
          id: "8",
          timestamp: "2025-12-29T17:25:00Z",
          category: "user",
          action: "Settings Updated",
          details: "User updated notification preferences",
          severity: "success",
          source: "user-settings",
          userId: "u2",
        },
      ];
      
      setLogs(mockLogs);
      
      // Simulate real-time updates
      if (isRealTime) {
        const interval = setInterval(() => {
          const newLog: LogEntry = {
            id: Date.now().toString(),
            timestamp: new Date().toISOString(),
            category: ["auth", "agi", "api", "mcp"][Math.floor(Math.random() * 4)],
            action: ["Activity", "Event", "Action", "Operation"][Math.floor(Math.random() * 4)],
            details: "Real-time log entry generated",
            severity: ["info", "success", "warning"][Math.floor(Math.random() * 3)] as any,
            source: "system",
          };
          setLogs(prev => [newLog, ...prev].slice(0, 100));
        }, 10000);
        
        return () => clearInterval(interval);
      }
    } catch (error) {
      console.error("Failed to load logs:", error);
      addLogEntry({
        category: "error",
        action: "Load Logs",
        details: String(error),
        severity: "error",
        source: "action-log",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.details.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.source.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || log.category === categoryFilter;
    const matchesSeverity = severityFilter === "all" || log.severity === severityFilter;
    return matchesSearch && matchesCategory && matchesSeverity;
  });

  const exportLogs = () => {
    const csv = [
      ["Timestamp", "Category", "Action", "Details", "Severity", "Source", "User ID"].join(","),
      ...filteredLogs.map(l => [
        new Date(l.timestamp).toISOString(),
        l.category,
        l.action,
        `"${l.details.replace(/"/g, '""')}"`,
        l.severity,
        l.source,
        l.userId || "",
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `action-log-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSeverityStats = () => {
    return {
      total: logs.length,
      info: logs.filter(l => l.severity === "info").length,
      warning: logs.filter(l => l.severity === "warning").length,
      error: logs.filter(l => l.severity === "error").length,
      success: logs.filter(l => l.severity === "success").length,
    };
  };

  const stats = getSeverityStats();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <ScrollText className="mx-auto h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Loading action logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Action Log</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">System activity and events</p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2">
            <div className={cn("h-2 w-2 rounded-full", isRealTime ? "bg-green-500 animate-pulse" : "bg-gray-500")} />
            <span className="text-sm">{isRealTime ? "Live" : "Paused"}</span>
            <button
              onClick={() => setIsRealTime(!isRealTime)}
              className="ml-2 rounded p-1 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
            >
              {isRealTime ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadLogs}
            className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={exportLogs}
            className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-5">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
          <p className="text-2xl font-bold">{stats.total}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Total</p>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-500" />
            <p className="text-2xl font-bold">{stats.info}</p>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Info</p>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <p className="text-2xl font-bold">{stats.success}</p>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Success</p>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <p className="text-2xl font-bold">{stats.warning}</p>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Warnings</p>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <p className="text-2xl font-bold">{stats.error}</p>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Errors</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-between rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>
        
        <div className="flex gap-2">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          >
            <option value="all">All Categories</option>
            {LOG_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          >
            <option value="all">All Severities</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="success">Success</option>
          </select>
        </div>
      </div>

      {/* Logs */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted)/.3)]">
              <tr className="text-left text-xs uppercase text-[hsl(var(--muted-foreground))]">
                <th className="px-6 py-3 font-medium w-32">Timestamp</th>
                <th className="px-6 py-3 font-medium w-20">Severity</th>
                <th className="px-6 py-3 font-medium w-24">Category</th>
                <th className="px-6 py-3 font-medium">Action</th>
                <th className="px-6 py-3 font-medium">Details</th>
                <th className="px-6 py-3 font-medium w-32">Source</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence mode="popLayout">
                {filteredLogs.map((log) => {
                  const SeverityIcon = SEVERITY_ICONS[log.severity];
                  return (
                    <motion.tr
                      key={log.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/.3)] transition-colors"
                    >
                      <td className="px-6 py-3 text-sm whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-6 py-3">
                        <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium", SEVERITY_COLORS[log.severity])}>
                          <SeverityIcon className="h-3 w-3" />
                          {log.severity}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <span className="rounded bg-[hsl(var(--muted))] px-2 py-0.5 text-xs font-mono">
                          {log.category}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm font-medium">
                        {log.action}
                      </td>
                      <td className="px-6 py-3 text-sm text-[hsl(var(--muted-foreground))] max-w-md truncate">
                        {log.details}
                      </td>
                      <td className="px-6 py-3 text-xs text-[hsl(var(--muted-foreground))]">
                        {log.source}
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {filteredLogs.length === 0 && (
          <div className="p-12 text-center text-[hsl(var(--muted-foreground))]">
            <ScrollText className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>No logs found matching your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}
